import axios from "axios";
import docProfileApi from "@/Services/doc-profile-api";
import type { SongOrderSummary } from "@/types/SongOrder";
import {
  getDocProfileUserIdVariants,
  isTempUserGuid,
  normalizeGuid,
  readCookie,
} from "./helper";
import { tempuserGuid } from "./constant";
import {
  loadOnboardingSession,
  type OnboardingSessionData,
} from "./onboardingSession";
import {
  resolveOccasionValue,
  resolveRecipientValue,
} from "./onboardingData";
import { fetchUserProfileAbridged, readProfileString } from "./userProfile";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

/** `customProps.customData` arrives as a JSON string. */
function parseJsonRecord(value: unknown): UnknownRecord | null {
  if (typeof value === "string") {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return asRecord(value);
}

function readString(record: UnknownRecord | null, key: string): string {
  const value = record?.[key];
  return typeof value === "string" ? value.trim() : "";
}

interface SongOrderDetails {
  recipientName: string;
  relationship: string;
  occasion: string;
  musicStyle: string;
}

/**
 * Song details come from the JSON blob on `customProps`, which is keyed
 * `metadata` on newer profiles and `customData` on older ones:
 * `{ songfor: "Partner", name: "Rosy", occasion: "Birthday", genre: "Pop", … }`
 */
export function mapDocProfileToOrderDetails(
  profile: UnknownRecord,
): SongOrderDetails {
  const customProps = parseJsonRecord(profile.customProps);
  // `||` not `??`: an empty `metadata` must fall through to the legacy blob.
  const songData = parseJsonRecord(
    customProps?.metadata || customProps?.customData,
  );

  return {
    recipientName: readString(songData, "name"),
    relationship: readString(songData, "songfor"),
    occasion: readString(songData, "occasion"),
    musicStyle: readString(songData, "genre"),
  };
}

export function mapOnboardingSessionToOrderSummary(
  session: OnboardingSessionData,
  confirmationEmail = "",
): SongOrderSummary {
  const relationship = resolveRecipientValue(session) ?? "";
  const recipientName =
    session.recipientName.trim() || relationship;
  const occasion = resolveOccasionValue(session) ?? "";
  const musicStyle =
    session.selectedGenres.length > 0
      ? session.selectedGenres.join(", ")
      : "";

  return {
    recipientName,
    relationship,
    occasion,
    musicStyle,
    confirmationEmail,
  };
}

/** The buyer's own address — not the nested guestUser email. */
async function fetchConfirmationEmail(userGuid: string): Promise<string> {
  const profile = await fetchUserProfileAbridged(userGuid);
  return readProfileString(profile, "email");
}

async function fetchDocProfileRecord(
  userGuid: string,
  docId: string,
): Promise<UnknownRecord | null> {
  const userIdVariants = getDocProfileUserIdVariants(userGuid);

  for (const userId of userIdVariants) {
    try {
      const docProfileResponse = await docProfileApi.GetDocProfile(userId, docId, {
        skipErrorPopup: true,
      });

      const profile =
        asRecord((docProfileResponse as { data?: unknown }).data) ??
        asRecord(docProfileResponse);

      if (profile) {
        return profile;
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status !== 404 && userId === userIdVariants[userIdVariants.length - 1]) {
        console.warn("Failed to load doc profile for order summary:", error);
      }
    }
  }

  return null;
}

const EMPTY_ORDER_SUMMARY: SongOrderSummary = {
  recipientName: "",
  relationship: "",
  occasion: "",
  musicStyle: "",
  confirmationEmail: "",
};

/**
 * Loads the order summary: song details from the doc-profile call when
 * available, otherwise from the guest onboarding session (browser Back
 * from signup).
 */
export async function fetchSongOrderSummary(
  docId: string,
  userId?: string | null,
): Promise<SongOrderSummary> {
  if (!docId) {
    throw new Error("A docId is required to load the order summary.");
  }

  const userGuid =
    normalizeGuid(userId || readCookie("userguid")) ||
    normalizeGuid(tempuserGuid);

  if (!userGuid) {
    throw new Error("A userId is required to load the order summary.");
  }

  let confirmationEmail = "";
  if (!isTempUserGuid(userGuid)) {
    confirmationEmail = await fetchConfirmationEmail(userGuid).catch(
      (error: unknown) => {
        console.error("Failed to load user profile for order summary:", error);
        return "";
      },
    );
  }

  const profile = await fetchDocProfileRecord(userGuid, docId);
  if (profile) {
    return {
      ...mapDocProfileToOrderDetails(profile),
      confirmationEmail,
    };
  }

  const session = loadOnboardingSession();
  if (session) {
    return mapOnboardingSessionToOrderSummary(session, confirmationEmail);
  }

  console.warn(
    "Order summary: doc profile unavailable and no onboarding session — showing empty summary.",
    { docId, userGuid },
  );
  return { ...EMPTY_ORDER_SUMMARY, confirmationEmail };
}
